import React, { useCallback, useState, Fragment } from "react";
import {
    Tab as MaterialUITab,
    Tabs as MaterialUITabs,
    Box,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { colors, THEME_DARK, THEME_WHITE } from "~/theme";

export interface Tab {
    label: string | JSX.Element;
    content: JSX.Element;
    target: string;
}
interface WithDarkTheme {
    darkTheme?: boolean;
}

const StyledMaterialUITabs = withStyles({
    flexContainer: {
        borderBottom: ({ darkTheme }: WithDarkTheme) =>
            `2px solid ${darkTheme ? colors.white10 : colors.tabsGrey}`,
    },
})(MaterialUITabs);
const StyledMaterialUITab = withStyles({
    root: {
        textTransform: "initial",
        fontSize: "18px",
        flexGrow: 1,
        "&:not(.Mui-selected)": {
            color: ({ darkTheme }: WithDarkTheme) =>
                darkTheme ? colors.white40 : colors.tabsGrey,
        },
        "&.Mui-selected": {
            color: ({ darkTheme }: WithDarkTheme) =>
                darkTheme ? colors.white : "inherit",
            borderBottom: ({ darkTheme }: WithDarkTheme) =>
                darkTheme ? `2px solid ${colors.white}` : "",
        },
    },
})(MaterialUITab);

export function Tabs({
    tabs,
    selectedTab,
    onChange,
    classes,
    theme,
}: {
    tabs: Tab[];
    selectedTab?: number;
    onChange?: (value: number, tab: Tab) => void;
    classes?: {
        tab?: string;
        tabContent?: string;
        container?: string;
    };
    theme?: typeof THEME_DARK | typeof THEME_WHITE;
}) {
    const [tabIndex, setTabIndex] = useState(selectedTab || 0);
    const handleTabChange = useCallback(
        (event: React.ChangeEvent<{}>, newValue: number) => {
            setTabIndex(newValue);
            onChange?.(newValue, tabs[newValue]);
        },
        [onChange, tabs],
    );

    return (
        <>
            <StyledMaterialUITabs
                className={classes?.container}
                value={tabIndex}
                onChange={handleTabChange}
                darkTheme={theme === THEME_DARK}
            >
                {tabs.map((tab) => (
                    <StyledMaterialUITab
                        data-target={`tab-${tab.target}`}
                        key={tab.target}
                        label={tab.label}
                        className={classes?.tab}
                        darkTheme={theme === THEME_DARK}
                    />
                ))}
            </StyledMaterialUITabs>
            {tabs.map((tab, index) => (
                <Fragment key={tab.target}>
                    {index === tabIndex && (
                        <Box className={classes?.tabContent} paddingTop="32px">
                            {tab.content}
                        </Box>
                    )}
                </Fragment>
            ))}
        </>
    );
}
